import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Refresh as RetryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  uploadProjectFile,
  uploadMoldFile,
} from '../../services/file.service';
import {
  FileCategory,
  FileUploadData,
  ProjectFile,
  getFileCategoryFromExtension,
  formatFileSize,
  MAX_FILE_SIZE,
  CATEGORY_ICONS,
  CATEGORY_COLORS,
} from '../../types/file.types';

// ===== 타입 정의 =====

/**
 * 업로드 상태
 */
type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

/**
 * 메타데이터를 포함한 파일 정보
 */
interface FileWithMetadata {
  id: string; // 고유 ID (임시)
  file: File;
  category: FileCategory;
  description: string;
  tags: string[];
  status: UploadStatus;
  progress: number;
  error?: string;
}

/**
 * Props
 */
interface FileUploadZoneProps {
  parentType: 'project' | 'mold';
  parentId: string;
  parentName?: string;
  open: boolean;
  onClose: () => void;
  onUploadComplete: (files: ProjectFile[]) => void;
}

// ===== 상수 =====

const FILE_CATEGORIES: FileCategory[] = [
  '설계도면',
  '3D모델',
  '문서',
  '사진',
  'CT결과서',
  'QR코드',
  '기타',
];

/**
 * FileUploadZone 컴포넌트
 * - 다중 파일 업로드
 * - 업로드 큐 관리
 * - 개별 진행률 표시
 */
export default function FileUploadZone({
  parentType,
  parentId,
  parentName,
  open,
  onClose,
  onUploadComplete,
}: FileUploadZoneProps) {
  const { authUser } = useAuth();

  // ===== State =====

  // 업로드할 파일 목록 (메타데이터 포함)
  const [files, setFiles] = useState<FileWithMetadata[]>([]);

  // 드래그 오버 상태
  const [dragOver, setDragOver] = useState(false);

  // 전체 에러 메시지
  const [globalError, setGlobalError] = useState<string | null>(null);

  // 업로드 진행 중 여부
  const [uploading, setUploading] = useState(false);

  // 업로드 완료 상태
  const [uploadComplete, setUploadComplete] = useState(false);

  // 업로드 완료된 파일들
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFile[]>([]);

  // ===== Helper Functions =====

  /**
   * 고유 ID 생성
   */
  const generateId = (): string => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * 파일 검증
   */
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `파일 크기가 너무 큽니다 (최대 ${MAX_FILE_SIZE / 1024 / 1024}MB)`;
    }
    return null;
  };

  /**
   * 파일 추가 (검증 포함)
   */
  const addFiles = useCallback((newFiles: File[]) => {
    const validFiles: FileWithMetadata[] = [];
    const errors: string[] = [];

    newFiles.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        const fileWithMetadata: FileWithMetadata = {
          id: generateId(),
          file,
          category: getFileCategoryFromExtension(file.name),
          description: '',
          tags: [],
          status: 'pending',
          progress: 0,
        };
        validFiles.push(fileWithMetadata);
      }
    });

    if (errors.length > 0) {
      setGlobalError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }
  }, []);

  // ===== Event Handlers =====

  /**
   * 파일 선택 버튼
   */
  const handleFileButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e: any) => {
      const selectedFiles = Array.from(e.target.files || []) as File[];
      if (selectedFiles.length > 0) {
        addFiles(selectedFiles);
      }
    };
    input.click();
  };

  /**
   * Drag & Drop
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  };

  /**
   * 개별 파일 삭제
   */
  const handleRemoveFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  /**
   * 파일 메타데이터 업데이트
   */
  const handleUpdateFile = (
    fileId: string,
    updates: Partial<Pick<FileWithMetadata, 'category' | 'description' | 'tags'>>
  ) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
    );
  };

  /**
   * 태그 추가
   */
  const handleAddTag = (fileId: string, tag: string) => {
    if (!tag.trim()) return;

    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId && !f.tags.includes(tag.trim())
          ? { ...f, tags: [...f.tags, tag.trim()] }
          : f
      )
    );
  };

  /**
   * 태그 삭제
   */
  const handleRemoveTag = (fileId: string, tag: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId ? { ...f, tags: f.tags.filter((t) => t !== tag) } : f
      )
    );
  };

  /**
   * 개별 파일 업로드
   */
  const uploadSingleFile = async (
    fileWithMeta: FileWithMetadata
  ): Promise<ProjectFile | null> => {
    if (!authUser) return null;

    const uploadData: FileUploadData = {
      file: fileWithMeta.file,
      category: fileWithMeta.category,
      description: fileWithMeta.description.trim() || undefined,
      tags: fileWithMeta.tags.length > 0 ? fileWithMeta.tags : undefined,
      version: 'v1.0',
    };

    const progressCallback = (progress: number) => {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithMeta.id ? { ...f, progress, status: 'uploading' } : f
        )
      );
    };

    try {
      // 업로드 시작
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithMeta.id ? { ...f, status: 'uploading', progress: 0 } : f
        )
      );

      let uploadedFile: ProjectFile;

      if (parentType === 'project') {
        uploadedFile = await uploadProjectFile(
          parentId,
          uploadData,
          authUser.uid,
          authUser.displayName || authUser.email || 'Unknown',
          parentName,
          progressCallback
        );
      } else {
        uploadedFile = await uploadMoldFile(
          parentId,
          uploadData,
          authUser.uid,
          authUser.displayName || authUser.email || 'Unknown',
          parentName,
          progressCallback
        );
      }

      // 성공
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithMeta.id ? { ...f, status: 'success', progress: 100 } : f
        )
      );

      return uploadedFile;
    } catch (err: any) {
      // 실패
      const errorMessage = err.message || '업로드 실패';
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithMeta.id
            ? { ...f, status: 'error', progress: 0, error: errorMessage }
            : f
        )
      );
      return null;
    }
  };

  /**
   * 전체 업로드 시작
   */
  const handleStartUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setGlobalError(null);
    setUploadedFiles([]);

    const results: ProjectFile[] = [];

    // 순차 업로드 (병렬 처리 시 Firebase 제한 가능성)
    for (const fileWithMeta of files) {
      if (fileWithMeta.status !== 'pending' && fileWithMeta.status !== 'error') {
        continue; // 이미 성공한 파일은 건너뛰기
      }

      const result = await uploadSingleFile(fileWithMeta);
      if (result) {
        results.push(result);
      }
    }

    setUploadedFiles(results);
    setUploading(false);

    // 모든 파일 업로드 완료 시 (results 배열 기반으로 체크)
    const allSuccess = results.length === files.length;
    const hasSuccess = results.length > 0;
    console.log('Upload complete check:', { 
      allSuccess, 
      hasSuccess,
      filesLength: files.length, 
      resultsLength: results.length,
      fileStatuses: files.map(f => ({ name: f.file.name, status: f.status }))
    });
    
    // 하나라도 성공했으면 확인 버튼 표시
    if (hasSuccess && files.length > 0) {
      console.log('Setting uploadComplete to true');
      setUploadComplete(true);
      onUploadComplete(results);
    }
  };

  /**
   * 실패한 파일만 재시도
   */
  const handleRetryFailed = async () => {
    const failedFiles = files.filter((f) => f.status === 'error');
    if (failedFiles.length === 0) return;

    setUploading(true);
    setGlobalError(null);

    const results: ProjectFile[] = [];

    for (const fileWithMeta of failedFiles) {
      const result = await uploadSingleFile(fileWithMeta);
      if (result) {
        results.push(result);
      }
    }

    setUploadedFiles((prev) => [...prev, ...results]);
    setUploading(false);

    // 재시도 후 모두 성공 (전체 성공한 파일 수로 체크)
    const totalSuccess = uploadedFiles.length + results.length;
    const allSuccess = totalSuccess === files.length;
    console.log('Retry complete check:', { 
      allSuccess, 
      totalSuccess,
      uploadedFilesLength: uploadedFiles.length,
      resultsLength: results.length,
      filesLength: files.length 
    });
    
    if (allSuccess && files.length > 0) {
      console.log('Setting uploadComplete to true after retry');
      setUploadComplete(true); // 업로드 완료 상태로 변경
      onUploadComplete([...uploadedFiles, ...results]);
      // handleClose()를 호출하지 않음 - 사용자가 확인 버튼을 눌러야 닫힘
    }
  };

  /**
   * 다이얼로그 닫기
   */
  const handleClose = () => {
    if (uploading) return; // 업로드 중에는 닫기 불가

    setFiles([]);
    setDragOver(false);
    setGlobalError(null);
    setUploading(false);
    setUploadComplete(false);
    setUploadedFiles([]);
    onClose();
  };

  /**
   * 업로드 완료 후 확인 및 페이지 새로고침
   */
  const handleConfirmAndReload = () => {
    handleClose();
    window.location.reload(); // 페이지 리로드
  };

  // ===== Computed Values =====

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const uploadingCount = files.filter((f) => f.status === 'uploading').length;
  const successCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  const overallProgress =
    files.length > 0
      ? files.reduce((sum, f) => sum + f.progress, 0) / files.length
      : 0;

  // ===== Render =====

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">파일 업로드</Typography>
          <IconButton
            onClick={handleClose}
            disabled={uploading}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* 전체 에러 메시지 */}
        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError(null)}>
            {globalError}
          </Alert>
        )}

        {/* 드래그 앤 드롭 영역 */}
        {files.length === 0 && (
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleFileButtonClick}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: dragOver ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.3s',
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              파일을 드래그하거나 클릭하여 선택
            </Typography>
            <Typography variant="body2" color="text.secondary">
              다중 파일 선택 가능 (최대 {MAX_FILE_SIZE / 1024 / 1024}MB/파일)
            </Typography>
          </Box>
        )}

        {/* 파일 목록 */}
        {files.length > 0 && (
          <Box>
            {/* 파일 추가 버튼 */}
            {!uploading && (
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleFileButtonClick}
                sx={{ mb: 2 }}
              >
                파일 추가
              </Button>
            )}

            {/* 전체 진행률 */}
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    전체 진행률
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(overallProgress)}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={overallProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {successCount}/{files.length} 파일 완료
                </Typography>
              </Box>
            )}

            {/* 상태 요약 */}
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Chip
                label={`대기: ${pendingCount}`}
                size="small"
                color="default"
              />
              {uploadingCount > 0 && (
                <Chip
                  label={`업로드 중: ${uploadingCount}`}
                  size="small"
                  color="primary"
                />
              )}
              {successCount > 0 && (
                <Chip
                  label={`완료: ${successCount}`}
                  size="small"
                  color="success"
                />
              )}
              {errorCount > 0 && (
                <Chip
                  label={`실패: ${errorCount}`}
                  size="small"
                  color="error"
                />
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* 개별 파일 카드 */}
            <Box display="flex" flexDirection="column" gap={2}>
              {files.map((fileWithMeta) => (
                <Paper
                  key={fileWithMeta.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor:
                      fileWithMeta.status === 'success'
                        ? 'success.lighter'
                        : fileWithMeta.status === 'error'
                        ? 'error.lighter'
                        : 'background.paper',
                  }}
                >
                  {/* 파일 헤더 */}
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <FileIcon color="primary" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {fileWithMeta.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(fileWithMeta.file.size)}
                      </Typography>
                    </Box>

                    {/* 상태 아이콘 */}
                    {fileWithMeta.status === 'uploading' && (
                      <Box sx={{ color: 'primary.main' }}>업로드 중...</Box>
                    )}
                    {fileWithMeta.status === 'success' && (
                      <SuccessIcon color="success" />
                    )}
                    {fileWithMeta.status === 'error' && (
                      <ErrorIcon color="error" />
                    )}

                    {/* 삭제 버튼 */}
                    {!uploading && fileWithMeta.status !== 'success' && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(fileWithMeta.id)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}

                    {/* 재시도 버튼 */}
                    {!uploading && fileWithMeta.status === 'error' && (
                      <IconButton
                        size="small"
                        onClick={() => uploadSingleFile(fileWithMeta)}
                        sx={{ ml: 1 }}
                      >
                        <RetryIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* 에러 메시지 */}
                  {fileWithMeta.status === 'error' && fileWithMeta.error && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {fileWithMeta.error}
                    </Alert>
                  )}

                  {/* 진행률 */}
                  {fileWithMeta.status === 'uploading' && (
                    <Box sx={{ mb: 1.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={fileWithMeta.progress}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(fileWithMeta.progress)}%
                      </Typography>
                    </Box>
                  )}

                  {/* 메타데이터 입력 */}
                  {fileWithMeta.status === 'pending' && !uploading && (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {/* 카테고리 */}
                      <TextField
                        select
                        label="카테고리"
                        value={fileWithMeta.category}
                        onChange={(e) =>
                          handleUpdateFile(fileWithMeta.id, {
                            category: e.target.value as FileCategory,
                          })
                        }
                        size="small"
                        fullWidth
                      >
                        {FILE_CATEGORIES.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: 20,
                                  color: CATEGORY_COLORS[cat],
                                }}
                              >
                                {CATEGORY_ICONS[cat]}
                              </span>
                              <span>{cat}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>

                      {/* 설명 */}
                      <TextField
                        label="설명 (선택)"
                        value={fileWithMeta.description}
                        onChange={(e) =>
                          handleUpdateFile(fileWithMeta.id, {
                            description: e.target.value,
                          })
                        }
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                      />

                      {/* 태그 */}
                      <Box>
                        <TextField
                          label="태그 (선택)"
                          size="small"
                          fullWidth
                          placeholder="Enter로 추가"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const input = e.target as HTMLInputElement;
                              handleAddTag(fileWithMeta.id, input.value);
                              input.value = '';
                            }
                          }}
                        />
                        {fileWithMeta.tags.length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                            {fileWithMeta.tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                onDelete={() =>
                                  handleRemoveTag(fileWithMeta.id, tag)
                                }
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* 완료된 파일 정보 요약 */}
                  {fileWithMeta.status === 'success' && (
                    <Box>
                      <Chip
                        label={fileWithMeta.category}
                        size="small"
                        sx={{
                          bgcolor: CATEGORY_COLORS[fileWithMeta.category],
                          color: 'white',
                        }}
                      />
                      {fileWithMeta.description && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          {fileWithMeta.description}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {uploadComplete ? (
          // 업로드 완료 시: 확인 버튼만 표시
          <Button
            variant="contained"
            color="success"
            startIcon={<SuccessIcon />}
            onClick={handleConfirmAndReload}
          >
            확인 (페이지 새로고침)
          </Button>
        ) : (
          // 업로드 진행 중 또는 대기 중: 기존 버튼들
          <>
            <Button onClick={handleClose} disabled={uploading}>
              취소
            </Button>

            {errorCount > 0 && !uploading && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<RetryIcon />}
                onClick={handleRetryFailed}
              >
                실패한 파일 재시도 ({errorCount})
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleStartUpload}
              disabled={files.length === 0 || uploading || successCount === files.length}
            >
              {uploading ? '업로드 중...' : `업로드 시작 (${pendingCount + errorCount})`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
